<template>
    <div id="app">
        <!--Arborescence-->
        <p class="arborescence">
            <a>FISI</a> <span class="sep">/</span>
            <a>Transcam</a> <span class="sep">/</span>
            <a class="onglet-actuel">Nouvelle Feuille de Soins</a>
        </p>

        <!--Titre de Page-->
        <h1 class="titre-page">Nouvelle Feuille de Soins</h1>

        <!--Bandeau reglementaire ADR (porte depuis Formfsoin UtiliseAdrText) : visible quand la
            facture est realisee a l'aide du service ADR (acquisition des droits en ligne).-->
        <div v-if="utiliseAdrVisible" class="bandeau-adr" role="status">
            <v-icon size="16" class="mr-1">mdi-shield-check</v-icon>{{ utiliseAdrText }}
        </div>

        <!--Informations Générales-->
        <div class="d-flex justify-space-between infos-generales">
            <div class="d-flex flex-column justify-space-around  infos-patient-praticien">

                <!--Patient-->
                <div class="d-flex mb-1 rounded-lg elevation-5 align-center carte-patient-praticien cursor-pointer" @click="drawer = true">
                    <div class="m-2 rounded-lg photo-profil-patient-praticien" style="background-color: #5AC2C2;">
                        <v-icon color="white" size="70">mdi-account</v-icon>
                    </div>
                    <div class="d-flex flex-column ml-3 mr-10 nom-patient-praticien" style="line-height: 1.1;">
                        <span class="font-weight-bold">Patient</span>
                        <span>{{ patientNomPrenom }}</span>
                    </div>
                </div>

                <!--Praticien-->
                <div class="d-flex mb-4 rounded-lg elevation-5 align-center carte-patient-praticien cursor-pointer" @click="drawer = true">
                    <div class="m-2 rounded-lg photo-profil-patient-praticien" style="background-color: #5AC2C2;">
                        <v-icon color="white" size="70">mdi-account</v-icon>
                    </div>
                    <div class="d-flex flex-column ml-3 mr-10 nom-patient-praticien" style="line-height: 1.1;">
                        <span class="font-weight-bold">Praticien</span>
                        <span>{{ praticienNomPrenom }}</span>
                    </div>
                </div>

                <!--Details praticien (carte CPS)-->
                <div v-if="praticienDetails" class="mx-3 mb-2">
                    <a class="lien-details-praticien" @click="detailsPraticienOuvert = !detailsPraticienOuvert">
                        {{ detailsPraticienOuvert ? 'Masquer les details' : 'Details praticien' }}
                    </a>
                    <v-expand-transition>
                        <div v-if="detailsPraticienOuvert" class="panneau-praticien pa-2 mt-1 rounded">
                            <div v-if="praticienDetails.rpps"><span class="lbl">RPPS / n. national :</span> {{ praticienDetails.rpps }}</div>
                            <div v-if="praticienDetails.specialite"><span class="lbl">Specialite :</span> {{ praticienDetails.specialite }}</div>
                            <div v-if="praticienDetails.convention"><span class="lbl">Convention :</span> {{ praticienDetails.convention }}</div>
                            <div v-if="praticienDetails.zoneTarifaire"><span class="lbl">Zone tarifaire :</span> {{ praticienDetails.zoneTarifaire }}</div>
                            <div v-if="praticienDetails.zoneIk"><span class="lbl">Zone IK :</span> {{ praticienDetails.zoneIk }}</div>
                            <div v-if="praticienDetails.identFacturation"><span class="lbl">Ident. facturation :</span> {{ praticienDetails.identFacturation }}</div>
                            <div v-if="praticienDetails.structure"><span class="lbl">Structure :</span> {{ praticienDetails.structure }}</div>
                        </div>
                    </v-expand-transition>
                </div>

                <div class="mx-3 parcours-soins">
                    <h8 class="font-weight-bold">Parcours de soins</h8>
                    <div class="d-flex flex-wrap ga-2 align-center mt-1 mb-2">
                        <v-checkbox v-model="jeMt" @change="onMtChange('je')" label="Je suis le med. traitant" density="compact" hide-details></v-checkbox>
                        <v-checkbox v-model="autreMt" @change="onMtChange('autre')" label="Autre M.T" density="compact" hide-details></v-checkbox>
                        <v-checkbox v-model="aucunMt" @change="onMtChange('aucun')" label="Aucun" density="compact" hide-details></v-checkbox>
                    </div>
                    <v-combobox
                        :key="medecinFieldKey"
                        v-model="selectedMedecin"
                        :items="medecins"
                        item-title="label"
                        return-object
                        label="Nom medecin"
                        density="compact"
                        hide-details
                        class="mb-2"
                        :disabled="!autreMt"
                        no-filter
                        :menu-props="{ maxHeight: 240 }"
                        @update:search="onRechercheMedecin"
                        @update:model-value="onChoixMedecin"
                        @keydown.delete="onSupprConfrereKey">
                        <template #append-inner>
                            <v-btn
                                v-if="selectedMedecin"
                                icon="mdi-delete"
                                variant="text"
                                density="compact"
                                size="small"
                                aria-label="Supprimer le confrere"
                                title="Supprimer le confrere"
                                @mousedown.stop.prevent="supprimerConfrereCourant"></v-btn>
                        </template>
                    </v-combobox>
                    <div class="d-flex align-center ga-2 mb-2">
                        <v-btn
                            size="small"
                            variant="tonal"
                            color="deep-purple"
                            prepend-icon="mdi-account-search"
                            :loading="imtLoading"
                            title="Interroger le medecin traitant (teleservice IMT)"
                            @click="interrogerImt">IMT</v-btn>
                        <v-chip v-if="imtEtat" :color="imtEtat.color" size="small" variant="tonal" :title="imtEtat.tip">
                            <v-icon start :icon="imtEtat.icon"></v-icon>{{ imtEtat.label }}
                        </v-chip>
                    </div>
                    <v-select v-model="selectedParcours" :items="parcours" label="Parcours" density="compact" hide-details @update:model-value="envoyerChoixParcours"></v-select>
                </div>
            </div>

            <div class="d-flex border-s-md w-50 infos-paiement">
                <div class="ml-10 colonne-1-infos-paiement">
                    <div class="mb-8">
                        <p>Montant Total</p>
                        <h1>{{ formatMontant(montantTotal) }}</h1>
                    </div>
                    <div class="mb-8">
                        <p>Tier Payant</p>
                        <h1>{{ formatMontant(montantTiersPayant) }}</h1>
                    </div>
                    <div>
                        <p>Solde à payer</p>
                        <h1>{{ formatMontant(soldeAPayer) }}</h1>
                    </div>
                </div>
                <div class="ml-10 colonne-2-infos-paiement">
                    <div>
                        <p>Payeur</p>
                        <v-select v-model="selected"
                                  :items="['Apple', 'Orange', 'Banana', 'Pear']"
                                  :list-props="{ bgColor: 'purple' }"
                                  item-color="yellow"
                                  multiple></v-select>
                    </div>
                    <div>
                        <p>Mode de règlement</p>
                        <v-select v-model="selected"
                                  :items="['Apple', 'Orange', 'Banana', 'Pear']"
                                  :list-props="{ bgColor: 'purple' }"
                                  item-color="yellow"
                                  multiple></v-select>
                    </div>
                    <div>
                        <p>Banque émettrice</p>
                        <v-select v-model="selected"
                                  :items="['Apple', 'Orange', 'Banana', 'Pear']"
                                  :list-props="{ bgColor: 'purple' }"
                                  item-color="yellow"
                                  multiple></v-select>
                    </div>
                </div>
            </div>

            <div class="d-flex mr-16 flex-column justify-center boutons-utils">
                <v-btn elevation="4">Facture</v-btn>
                <v-btn elevation="4">Divers</v-btn>
                <v-btn elevation="4">Imprimer Feuille</v-btn>
                <v-btn elevation="4" @click="dialogComplementaire = true">Part Complémentaire</v-btn>
                <v-btn elevation="4" @click="dialogFormulesSTS = true">Formules STS</v-btn>
                <v-btn elevation="4" color="#119393" :loading="enregistrementEnCours" @click="enregistrer">Enregistrer</v-btn>
            </div>
        </div>
  
        <!--Actes effectués-->
        <div class="mt-10">
            <!--Titre de Partie-->
            <h2 class="font-weight-bold">Actes effectués</h2>

            <!--Une ligne d'actes = 1 ou plusieurs actes NGAP/CCAM-->
            <div class="mt-10" v-for="(ligne, ligneIndex) in lignesActes" :key="ligneIndex">
                <!--Actions-->
                <div>
                    <v-icon class="ml-2 mr-2">mdi-calendar-range</v-icon> <span class="mr-4 font-weight-bold">{{ ligne.date }}</span> <span>l</span>
                    <v-icon class="ml-2 mr-2">mdi-content-save-all</v-icon> <span class="mr-4 font-weight-bold">Enregistrer la saisie</span> <span>l</span>
                    <v-icon class="ml-2 mr-2" style="cursor:pointer" @click="supprimerLigne(ligneIndex)">mdi-trash-can-outline</v-icon> <span class="mr-4 font-weight-bold">Supprimer la saisie</span> <span>l</span>
                    <v-icon class="ml-2 mr-2">mdi-dots-horizontal</v-icon> <span class="mr-4 font-weight-bold">Détails</span>
                </div>

                <!--Actes-->
                <div class="bg-grey-lighten-3 mt-4 pa-3 d-flex ga-6 rounded-lg ligne-actes">
                    <!--Acte-->
                    <div class="d-flex flex-column justify-lg-space-between ga-3"
                         v-for="(acte, acteIndex) in ligne.actes" :key="acteIndex">
                        <div class="carre-acte elevation-5"
                             :class="[acte.type === 'NGAP' ? 'border-pink' : 'border-green',
                                      estDetailOuvert(ligneIndex, acteIndex) ? 'carre-acte-actif' : '']"
                             @click="toggleDetail(ligneIndex, acteIndex)">
                            <span v-if="acteEstForce(acte)" class="pastille-force"
                                  title="Valeur forcee"></span>
                            <span class="text-body-2">{{ acte.type }}</span>
                            <span class="font-weight-bold">{{ acte.code }}</span>
                            <span class="text-body-2">{{ formatMontant(acte.montant) }}</span>
                        </div>
                        <div class="supp-carre-acte elevation-2" @click="supprimerActe(ligneIndex, acteIndex)">
                            <span class="text-body-2">Supprimer</span>
                        </div>
                    </div>

                    <!--Add Acte-->
                    <div class="border-s-thin">
                        <div class="add-acte elevation-2 bg-white ml-6" @click="ouvrirAjoutActe(ligneIndex)">
                            <span class="font-weight-bold">+</span>
                        </div>
                    </div>
                </div>

                <!-- Detail de l'acte (un seul ouvert a la fois), sous la ligne entiere -->
                <v-expand-transition>
                    <div v-if="detailOuvert && detailOuvert.ligne === ligneIndex"
                         class="panneau-detail mt-2 pa-4 rounded-lg elevation-2"
                         :class="acteDetailOuvert() && acteDetailOuvert().type === 'NGAP'
                                 ? 'detail-pink' : 'detail-green'">
                        <div class="d-flex align-center mb-3">
                            <span class="font-weight-bold">
                                Detail {{ acteDetailOuvert() && acteDetailOuvert().type }}
                                — {{ acteDetailOuvert() && acteDetailOuvert().code }}
                            </span>
                            <v-spacer></v-spacer>
                            <v-btn size="small" variant="text" @click="detailOuvert = null">Fermer</v-btn>
                        </div>

                        <div class="grille-detail" v-if="acteDetailOuvert()">
                            <div v-for="champ in champsDetailOuvert()" :key="champ.key" class="champ-detail"
                                 :class="['taille-' + (champ.taille || 'md'),
                                          champ.forcable && acteDetailOuvert().detail.force
                                            && acteDetailOuvert().detail.force[champ.forcable] ? 'champ-force' : '']">
                                <label class="text-body-2 font-weight-bold">
                                    {{ champ.label }}
                                    <span v-if="champ.forcable && acteDetailOuvert().detail.force
                                                && acteDetailOuvert().detail.force[champ.forcable]"
                                          class="badge-force">force</span>
                                </label>

                                <!-- Cas special : champ "detailDent" = input texte + bouton schema dentaire -->
                                <div v-if="champ.type === 'text' && champ.key === 'detailDent'"
                                     class="d-flex align-center ga-1">
                                    <input v-model="acteDetailOuvert().detail[champ.key]"
                                           class="champ-code-acte" />
                                    <v-btn icon="mdi-tooth-outline" size="small" variant="text"
                                           color="#119393" title="Choisir les dents"
                                           @click="dialogDents = true"></v-btn>
                                </div>

                                <!-- Champ forcable : marque le flag de forcage a la saisie -->
                                <input v-else-if="champ.type === 'text' && champ.forcable"
                                       v-model="acteDetailOuvert().detail[champ.key]"
                                       @input="acteDetailOuvert().detail.force[champ.forcable] = true"
                                       class="champ-code-acte" />

                                <input v-else-if="champ.type === 'text'"
                                       v-model="acteDetailOuvert().detail[champ.key]"
                                       class="champ-code-acte" />

                                <input v-else-if="champ.type === 'date'"
                                       :value="acteDetailOuvert().detail[champ.key]"
                                       @input="onSaisieDate(champ.key, $event)"
                                       v-date-fr
                                       :disabled="!dateEpActive()"
                                       maxlength="10"
                                       placeholder="jj/mm/aaaa"
                                       class="champ-code-acte champ-date"
                                       :class="{ 'champ-date-invalide': !dateValide(acteDetailOuvert().detail[champ.key]), 'champ-readonly': !dateEpActive() }" />

                                <v-checkbox v-else-if="champ.type === 'checkbox'"
                                            v-model="acteDetailOuvert().detail[champ.key]"
                                            density="compact" hide-details></v-checkbox>

                                <select v-else-if="champ.type === 'combo'"
                                        v-model="acteDetailOuvert().detail[champ.key]"
                                        class="champ-code-acte champ-combo"
                                        @change="onChangeCombo(champ)">
                                    <option v-for="opt in optionsPourChamp(champ)" :key="opt.valeur" :value="opt.valeur">{{ opt.libelle }}</option>
                                </select>

                                <input v-else
                                       :value="acteDetailOuvert().detail[champ.key]"
                                       class="champ-code-acte champ-readonly" disabled />
                            </div>
                        </div>

                        <div class="d-flex mt-4 align-center ga-2">
                            <v-btn variant="outlined" color="#119393" @click="ouvrirPartAMO">
                                Montant remb. AMO
                            </v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="#119393" @click="appliquerDetail">Appliquer</v-btn>
                        </div>

                        <!-- Fenetre de selection des dents (schema dentaire) -->
                        <v-dialog v-model="dialogDents" max-width="700">
                            <SelecteurDents v-if="dialogDents && acteDetailOuvert()"
                                            :model-value="acteDetailOuvert().detail.detailDent"
                                            @update:model-value="val => { if (acteDetailOuvert()) acteDetailOuvert().detail.detailDent = val }"
                                            @close="dialogDents = false" />
                        </v-dialog>
                    </div>
                </v-expand-transition>
            </div>

            <!--Ajouter une ligne-->
            <v-btn class="mt-6" variant="outlined" color="#119393" @click="ajouterLigne">
                + Ajouter une ligne d'actes
            </v-btn>
        </div>

        <!-- Volet latéral -->
        <v-navigation-drawer v-model="drawer"
                                location="right"
                                temporary
                                width="500">
            <v-toolbar flat>
                <v-toolbar-title>Mon volet</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn icon @click="drawer = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-toolbar>

            <v-divider></v-divider>

            <Patient />
        </v-navigation-drawer>
    </div>
    
    <v-container class="pa-4">

    <!-- Popup recherche / ajout d'un acte (NGAP ou CCAM) -->
    <v-dialog v-model="dialogActe" max-width="500">
      <v-card>
        <v-card-title>Ajouter un acte</v-card-title>

        <v-card-text>
          <!-- Type d'acte -->
          <div class="d-flex ga-4 mb-4">
            <v-btn :variant="nouvelActe.type === 'NGAP' ? 'flat' : 'outlined'"
                   color="#E91E63"
                   @click="nouvelActe.type = 'NGAP'">NGAP</v-btn>
            <v-btn :variant="nouvelActe.type === 'CCAM' ? 'flat' : 'outlined'"
                   color="#4CAF50"
                   @click="nouvelActe.type = 'CCAM'">CCAM</v-btn>
          </div>

          <!-- Code acte (datalist d'autocomplete pour le CCAM) -->
          <label class="font-weight-bold">Code acte</label>
          <input v-model="nouvelActe.code"
                 list="ccam-codes"
                 maxlength="7"
                 class="champ-code-acte"
                 placeholder="ex : G (NGAP) ou EJQM004 (CCAM)"
                 @input="nouvelActe.code = nouvelActe.code.toUpperCase()"
                 @keyup.enter="validerAjoutActe" />
          <datalist id="ccam-codes" v-if="nouvelActe.type === 'CCAM'">
            <option v-for="(label, key) in codesCCAM" :key="key" :value="label" />
          </datalist>

          <p v-if="rechercheError" class="erreur-acte">{{ rechercheError }}</p>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dialogActe = false">Annuler</v-btn>
          <v-btn :loading="rechercheLoading" color="#119393" @click="validerAjoutActe">Ajouter</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Popup gestion des complementaires (port de fcompmanuel.vb) -->
    <v-dialog v-model="dialogComplementaire" max-width="760">
      <GestionComplementaire v-if="dialogComplementaire" @close="dialogComplementaire = false" />
    </v-dialog>

    <!-- Popup forcage du montant remboursable AMO d'une prestation (frmInfosCompPresta.vb) -->
    <v-dialog v-model="dialogPartAMO" max-width="560">
      <InfosCompPresta v-if="dialogPartAMO && actePartAMO"
                       :acte="actePartAMO"
                       :specialite="(praticienDetails && praticienDetails.specialite) || ''"
                       @close="dialogPartAMO = false" />
    </v-dialog>

    <!-- Popup formules & details complementaires - moteur STS (frmSTS.vb) -->
    <v-dialog v-model="dialogFormulesSTS" max-width="1000">
      <FormulesSTS v-if="dialogFormulesSTS" @close="dialogFormulesSTS = false" />
    </v-dialog>

    <!-- Message de retour apres enregistrement de la FSE -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="4000" location="top">
      {{ snackbarText }}
      <template #actions>
        <v-btn variant="text" @click="snackbar = false">Fermer</v-btn>
      </template>
    </v-snackbar>

    <!-- Question ordonnance SCOR apres enregistrement (port frmChoixOrdo). -->
    <DialogPiecesScor v-model:ouvert="scorOuvert" :noFse="scorNoFse" :identPatient="scorIdent"
                      titre="Joindre une ordonnance à la FSE" />
  </v-container>

</template>

<style scoped>
    *{
        color: #434343;
    }

    #app {
        /*padding: 30px 60px;*/
        padding: 30px 60px 150px 60px;
    }

    .lien-details-praticien { color: #119393; cursor: pointer; font-size: 0.85rem; font-weight: bold; }
    .panneau-praticien { background: #f5f5f5; font-size: 0.85rem; line-height: 1.5; }
    .panneau-praticien .lbl { font-weight: bold; color: #555; }

    .arborescence {
        color: #AFADAD;
    }

    .sep {
        margin: 0 8px;
    }

    .onglet-actuel {
        color: #5AC2C2 !important;
    }

    .titre-page {
        font-weight: bold;
        margin-bottom: 40px;
    }

    /* Bandeau reglementaire ADR (port de Formfsoin UtiliseAdrText) */
    .bandeau-adr {
        display: flex;
        align-items: center;
        margin: -24px 0 24px;
        padding: 6px 12px;
        font-size: 0.8rem;
        color: #0d5f5f;
        background-color: #e3f4f4;
        border-left: 3px solid #119393;
        border-radius: 4px;
    }

    .boutons-utils {
        gap: 20px;
    }

    .v-btn {
        color: #119393;
    }

    .carre-acte {
        width: 140px;
        height: 130px;
        border-radius: 8px;
        padding: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        position: relative;
    }

    .pastille-force {
        position: absolute;
        top: 6px;
        right: 6px;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background: #E8A33D;
    }

    .champ-force input {
        border-color: #E8A33D;
        background: #fff8ec;
    }

    .badge-force {
        font-size: 11px;
        color: #E8A33D;
        font-weight: bold;
    }

    .supp-carre-acte {
        width: 140px;
        border-radius: 8px;
        padding:4px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    .border-pink {
        border: 3px solid #E91E63;
    }

    .border-green {
        border: 3px solid #4CAF50;
    }

    .add-acte {
        width: 40px;
        height: 100%;
        border-radius: 8px;
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
    }

    /* Champ code acte dans la popup */
    .champ-code-acte {
        width: 100%;
        margin-top: 6px;
        padding: 10px;
        border: 1px solid #ccc;
        border-radius: 6px;
        font-size: 1rem;
        outline: none;
    }
    .champ-code-acte:focus {
        border-color: #119393;
    }
    .erreur-acte {
        color: #E91E63;
        font-weight: bold;
        margin-top: 10px;
    }

    .carre-acte-actif {
        outline: 3px solid #119393;
        outline-offset: 2px;
    }
    .panneau-detail {
        background-color: #fafafa;
    }
    .detail-pink { border-left: 4px solid #E91E63; }
    .detail-green { border-left: 4px solid #4CAF50; }
    .grille-detail {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
    .champ-detail {
        display: flex;
        flex-direction: column;
    }
    .champ-detail.taille-xs { width: 90px; }
    .champ-detail.taille-sm { width: 140px; }
    .champ-detail.taille-md { width: 200px; }
    .champ-combo {
        /* hauteur auto + min : evite le texte coupe verticalement sur mobile
           (height fixe + padding du champ-code-acte ne laissaient aucune marge) */
        height: auto;
        min-height: 42px;
        line-height: 1.3;
        background: #fff;
    }
    .champ-readonly { background: #f0f0f0; color: #666; }
    .champ-date-invalide { border-color: #E53935 !important; color: #E53935; }
    .valeur-readonly {
        padding: 8px 0;
        color: #888;
    }

    /* ===== Adaptation smartphone ===== */
    @media (max-width: 800px) {
        #app {
            padding: 16px 16px 120px 16px;
        }

        .titre-page {
            margin-bottom: 24px;
        }

        /* Les 3 colonnes (patient/praticien, paiement, boutons) s'empilent */
        .infos-generales {
            flex-direction: column;
            gap: 24px;
        }

        /* Cartes patient/praticien en pleine largeur, sans marges droites enormes */
        .infos-patient-praticien {
            width: 100%;
        }
        .nom-patient-praticien {
            margin-right: 0 !important;
        }

        /* Zone paiement : pleine largeur, sous-colonnes empilees, plus de bordure laterale */
        .infos-paiement {
            width: 100% !important;
            flex-direction: column;
            border-inline-start: none !important;
            gap: 16px;
        }
        .colonne-1-infos-paiement,
        .colonne-2-infos-paiement {
            margin-left: 0 !important;
            width: 100%;
        }

        /* Boutons d'action en pleine largeur */
        .boutons-utils {
            margin-right: 0 !important;
        }

        /* Une ligne d'actes peut contenir plusieurs actes (NGAP/CCAM) :
           on la fait defiler horizontalement au lieu de deborder la page. */
        .ligne-actes {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
        }
        /* Les cartes d'acte ne se compriment pas dans le defilement */
        .ligne-actes .carre-acte,
        .ligne-actes .supp-carre-acte {
            flex-shrink: 0;
        }
        .ligne-actes > div {
            flex-shrink: 0;
        }

        /* Le panneau de detail passe en une seule colonne sur mobile :
           chaque champ prend toute la largeur quelle que soit sa taille. */
        .champ-detail.taille-xs,
        .champ-detail.taille-sm,
        .champ-detail.taille-md {
            width: 100%;
        }

        /* Le volet lateral droit (width=500) ne doit pas depasser l'ecran */
        :deep(.v-navigation-drawer) {
            max-width: 100vw !important;
        }
    }
</style>

<script>
    import { defineComponent } from 'vue';
    import Patient from './Patient.vue';
    import SelecteurDents from './SelecteurDents.vue';
    import GestionComplementaire from './GestionComplementaire.vue';
    import InfosCompPresta from './InfosCompPresta.vue';
    import FormulesSTS from './FormulesSTS.vue';
    import Acte from '../script/Acte.js';
    import Fse from '../script/Fse.js';
    import Scor from '../script/Scor.js';
    import Assertion from '../script/Assertion.js';
    import DialogPiecesScor from './DialogPiecesScor.vue';
    import ParcoursDeSoin from '../script/TransCAM/ParcoursDeSoin.js';
    import {
        CHAMPS_NGAP, CHAMPS_CCAM, detailVide,
        detailDepuisPrest, appliquerDetailDansPrest,
    } from '../script/DetailActe.js';
    import { porteur, champ, CPS_NUMERO_IDENT, CPS_CODE_SPECIALITE,
             CPS_CODE_CONVENTION, CPS_ZONE_TARIFAIRE, CPS_ZONE_IK,
             CPS_IDENT_FACTURATION, CPS_RAISON_SOCIALE_STRUCTURE } from '@/script/champsCPS';

    // Code NGAP injecte en 1re ligne a l'ouverture, tant que la saisie d'actes n'ecrit pas
    // encore dans facfse.prest. Le back (enregreg_Click -> ssvFSE.controlechampsfse) refuse la
    // FSE si facfse.prest[1].presta_Renamed est vide ("Aucune prestation saisie dans la premiere
    // ligne"). "C" = consultation ; a changer ici si l'acte n'existe pas dans la base du praticien.
    const ACTE_TEST_NGAP = 'C';

    export default defineComponent({
        components: {
            Patient,
            SelecteurDents,
            GestionComplementaire,
            InfosCompPresta,
            FormulesSTS,
            DialogPiecesScor,
        },

        data() {
            return {
                drawer: false,

                // Panneau depliable "details praticien" (carte CPS)
                detailsPraticienOuvert: false,

                // Lignes d'actes : chaque ligne contient 1 ou plusieurs actes (NGAP ou CCAM).
                // Donnees d'exemple au depart ; ajout/suppression branches sur le back.
                lignesActes: [
                    {
                        date: this.dateDuJour(),
                        actes: [
                            { type: 'NGAP', code: 'G', libelle: 'Consultation', montant: 26.5 },
                            { type: 'CCAM', code: 'EJQM004', libelle: 'Acte CCAM', montant: 26.5 },
                        ],
                    },
                    {
                        date: this.dateDuJour(),
                        actes: [
                            { type: 'CCAM', code: 'EJQM004', libelle: 'Acte CCAM', montant: 26.5 },
                        ],
                    },
                ],

                // Detail ouvert : { ligne, acte } ou null (un seul a la fois)
                detailOuvert: null,

                // Fenetre de selection des dents (schema dentaire)
                dialogDents: false,

                // Popup gestion des complementaires (bouton "Part Complementaire")
                dialogComplementaire: false,

                // Popup forcage du montant remboursable AMO (par prestation)
                dialogPartAMO: false,
                actePartAMO: null,

                // Popup formules & details complementaires (STS)
                dialogFormulesSTS: false,

                // Listes de choix dynamiques CCAM (referentiel back) pour l'acte ouvert
                listesChoixCCAM: null,
                chargementChoix: false,

                // Popup d'ajout d'acte
                dialogActe: false,
                ligneCibleIndex: null,
                nouvelActe: { type: 'NGAP', code: '', libelle: '', montant: 0 },
                rechercheError: '',
                rechercheLoading: false,

                // Liste des codes CCAM pour l'autocomplete (chargee au montage)
                codesCCAM: {},

                // Tiers payant (statique pour l'instant, a brancher plus tard)
                montantTiersPayant: 0,

                enregistrementEnCours: false,

                // Question ordonnance SCOR apres enregistrement (port frmChoixOrdo).
                scorOuvert: false,
                scorNoFse: 0,
                scorIdent: 0,

                // Parcours de soins (porte depuis Fse.vue)
                selectedMedecin: null,
                medecins: [],          // confreres charges via getListeOriente : [{ nom, prenom, label }]
                medecinFieldKey: 0,    // force le remontage de l'autocomplete pour vider le texte affiche
                parcours: [],          // options reelles du backend
                selectedParcours: '',
                jeMt: false, autreMt: false, aucunMt: false,

                // IMT (Interrogation Medecin Traitant) : etat du bouton de re-interrogation manuelle.
                imtLoading: false,

                // Bandeau reglementaire ADR (Formsoin.UtiliseAdrText / UtiliseAdrTextVisible)
                utiliseAdrText: '',
                utiliseAdrVisible: false,

                // Message de retour apres enregistrement de la FSE
                snackbar: false,
                snackbarText: '',
                snackbarColor: 'success',
            };
        },

        watch: {
            // Reflet de l'ouverture du dialog Complementaire dans le store : permet
            // au dispatcher natif (App.vue) de savoir s'il faut ouvrir ou remplir.
            dialogComplementaire(val) {
                this.$store.commit('FSEHandler/setDialogComplementaireOuvert', val);
            },
            // AMC scannee cote natif alors que l'ecran est ferme -> ouverture du
            // dialog pre-rempli (GestionComplementaire consomme l'AMC en attente).
            amcEnAttente(val) {
                if (val && !this.dialogComplementaire) {
                    this.dialogComplementaire = true;
                }
            },
            // Resultat IMT arrive (poll Vitale/degrade termine) : pre-remplit le medecin oriente.
            medecinIMT() {
                this.appliquerResultatIMT(false);
            },
        },

        computed: {
            champsNGAP() { return CHAMPS_NGAP; },
            champsCCAM() { return CHAMPS_CCAM; },
            // AMC en attente (scannee cote natif), cf. store FSEHandler.
            amcEnAttente() { return this.$store.getters['FSEHandler/getAmcEnAttente']; },

            // Resultat IMT courant (medecin traitant retourne par le teleservice).
            medecinIMT() { return this.$store.getters['ChoixBenecStore/getMedecinIMT']; },

            // Etat visuel du bouton/chip IMT, fidele aux images btnIMT du desktop (IMTi / IMTi_interrogation
            // / imti_error). Discrimination par code retour : "00"=trouve, "10"=non trouve, autre=erreur.
            imtEtat() {
                const med = this.medecinIMT;
                if (!med) return null;
                const exec = med.statutExec || '';
                // IMT jamais lance / desactive (appelAuto_IMT=0) et aucun retour : pas de pastille.
                if (!med.code && (exec === '' || exec === 'NonLancé')) return null;
                if (exec === 'EnCoursDExecution') {
                    return { color: 'grey', icon: 'mdi-timer-sand', label: 'IMT en cours…', tip: 'Interrogation du medecin traitant en cours' };
                }
                if (med.code === '00' || (med.statutResult || '') === 'MédecinTrouvé') {
                    const nom = (med.nom + ' ' + med.prenom).trim();
                    return { color: 'success', icon: 'mdi-account-check', label: nom ? ('MT : ' + nom) : 'MT trouve', tip: med.message || 'Medecin traitant trouve' };
                }
                if (med.code === '10' || (med.statutResult || '') === 'MédecinNonTrouvé') {
                    return { color: 'warning', icon: 'mdi-account-question', label: 'Aucun MT declare', tip: med.message || 'Medecin traitant non trouve' };
                }
                return { color: 'error', icon: 'mdi-alert', label: 'IMT indisponible', tip: med.erreurTexte || med.message || 'Erreur lors de l\'interrogation du medecin traitant' };
            },

            montantTotal() {
                return this.lignesActes.reduce((total, ligne) => {
                    return total + ligne.actes.reduce((s, a) => s + (Number(a.montant) || 0), 0);
                }, 0);
            },
            soldeAPayer() {
                return this.montantTotal - (Number(this.montantTiersPayant) || 0);
            },

            // --- Patient (carte vitale, store SSV) : meme logique que Fse.vue ---
            enumVitale() {
                return this.$store.getters['SSV/getStructures_publiques_cartevitale'];
            },
            carteVitale() {
                return this.$store.getters['SSV/getCarteVitale'];
            },
            indexBenefActif() {
                return this.$store.getters['SSV/getIndexBenefActif'];
            },
            benefList() {
                const list = [];
                const cv = this.carteVitale;
                const e = this.enumVitale;
                if (cv?.benef?.length && e) {
                    for (let i = 0; i < cv.benef.length; i++) {
                        const b = cv.benef[i];
                        if (b?.FieldsCount > 0) {
                            list.push({
                                nom: b.Fields?.[e.VITALE_NOM_USUEL] ?? '',
                                prenom: b.Fields?.[e.VITALE_PRENOM] ?? '',
                            });
                        }
                    }
                }
                return list;
            },
            // Patient selectionne depuis la liste Patients ("+ Creer FSE")
            patientCourant() {
                return this.$store.getters['FSEHandler/getPatientCourant'];
            },
            patientNomPrenom() {
                // 1) patient choisi explicitement depuis la liste Patients
                const p = this.patientCourant;
                if (p && (p.Prenom || p.Nom)) {
                    return `${p.Prenom ?? ''} ${p.Nom ?? ''}`.trim();
                }
                // 2) sinon, beneficiaire actif de la carte vitale
                const idx = this.indexBenefActif ?? 0;
                const b = this.benefList[idx] || this.benefList[0];
                if (b) {
                    return `${b.prenom} ${b.nom}`.trim();
                }
                return 'Aucun patient (carte vitale non lue)';
            },

            // --- Praticien (carte CPS, store Cartes) : meme logique que Fse.vue ---
            carteCPS() {
                return this.$store.getters['Cartes/getCarteCPS'];
            },
            praticienNomPrenom() {
                const cps = this.carteCPS;
                // GetCPS peut renvoyer DEUX formes : un OBJET { FieldsCount, Fields } (porteur,
                // cf Fse.vue) OU un TABLEAU de groupes ({ group, Fields }). On gere les deux.
                // Groupe/porteur (PS) : Fields[5]=nom, Fields[6]=prenom, Fields[2]=RPPS.
                let fields = null;
                if (Array.isArray(cps)) {
                    const groupePorteur = cps.find((g) => g?.group === 1 || g?.Type === 1) || cps[0];
                    fields = groupePorteur?.Fields;
                } else if (cps && cps.Fields) {
                    fields = cps.Fields;
                }
                if (fields?.length > 6) {
                    const nom = String(fields[5] ?? '').replace(/[0-9]/g, '').trim();
                    const prenom = String(fields[6] ?? '').trim();
                    const complet = `${nom} ${prenom}`.trim();
                    if (complet) return `Dr ${complet}`;
                }
                // Pas de carte CPS lue -> repli sur le praticien en BASE (connexion id/mdp).
                const pb = this.$store.getters['ConnexionStore/getPraticien'];
                if (pb && (pb.nom || pb.prenom)) {
                    const complet = `${pb.prenom ?? ''} ${pb.nom ?? ''}`.trim();
                    if (complet) return `Dr ${complet}`;
                }
                return 'Aucun praticien (carte CPS non lue)';
            },
            // Details praticien extraits de la carte CPS (null si pas de CPS lue).
            // Bloc PORTEUR (group 1) : Fields[2]=RPPS. Bloc SITUATION (group 2) :
            // Fields[12]=convention, [13]=specialite, [14]=zone tarif, [15]=zone IK,
            // [8]=ident facturation, [7]=raison sociale structure.
            praticienDetails() {
                const cps = this.carteCPS;
                if (!cps) return null;
                const porteurBloc = porteur(cps);
                const situation = this.$store.getters['Cartes/getSituationChoisie'];
                const pf = (porteurBloc && porteurBloc.Fields) || [];
                const v = (arr, i) => (arr && arr[i] != null ? String(arr[i]).trim() : '');
                const s = (i) => champ(situation, i);
                const d = {
                    rpps: v(pf, CPS_NUMERO_IDENT),
                    specialite: s(CPS_CODE_SPECIALITE),
                    convention: s(CPS_CODE_CONVENTION),
                    zoneTarifaire: s(CPS_ZONE_TARIFAIRE),
                    zoneIk: s(CPS_ZONE_IK),
                    identFacturation: s(CPS_IDENT_FACTURATION),
                    structure: s(CPS_RAISON_SOCIALE_STRUCTURE),
                };
                const auMoinsUn = Object.values(d).some(x => x !== '');
                return auMoinsUn ? d : null;
            },
        },

        methods: {
            dateDuJour() {
                const d = new Date();
                const jj = String(d.getDate()).padStart(2, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                return `${jj}/${mm}/${d.getFullYear()}`;
            },

            formatMontant(valeur) {
                return (Number(valeur) || 0).toFixed(2) + '€';
            },

            // Convertit un prix back ("26,50", "26.50", nombre) en Number.
            parseMontant(valeur) {
                if (valeur == null) return 0;
                const n = parseFloat(String(valeur).replace(',', '.').replace(/[^\d.-]/g, ''));
                return isNaN(n) ? 0 : n;
            },

            ouvrirAjoutActe(ligneIndex) {
                this.ligneCibleIndex = ligneIndex;
                this.nouvelActe = { type: 'NGAP', code: '', libelle: '', montant: 0 };
                this.rechercheError = '';
                this.dialogActe = true;
            },

            async validerAjoutActe() {
                const code = (this.nouvelActe.code || '').trim();
                if (!code) {
                    this.rechercheError = 'Saisissez un code acte.';
                    return;
                }
                this.rechercheLoading = true;
                this.rechercheError = '';
                try {
                    let libelle = '';
                    let montant = 0;

                    if (this.nouvelActe.type === 'NGAP') {
                        const r = await Acte.getInfosActeNGAP(code);
                        if (!r || !r.Item2) {
                            this.rechercheError = (r && r.Item1) || 'Acte NGAP introuvable.';
                            return;
                        }
                        if (r.Item1) {
                            this.rechercheError = r.Item1;
                            return;
                        }
                        libelle = r.Item2.libelle || code;
                        montant = this.parseMontant(r.Item2.prixSs);
                    } else {
                        const r = await Acte.getInfoActeCCAM(code);
                        if (!r || !r.Item2) {
                            this.rechercheError = (r && r.Item1) ? ('Erreur : ' + r.Item1) : 'Acte CCAM introuvable.';
                            return;
                        }
                        if (r.Item1) {
                            this.rechercheError = 'Erreur : ' + r.Item1;
                            return;
                        }
                        libelle = r.Item2.libelle || code;
                        montant = this.parseMontant(r.Item2.prix);
                    }

                    const ligne = this.lignesActes[this.ligneCibleIndex];
                    if (ligne) {
                        ligne.actes.push({ type: this.nouvelActe.type, code, libelle, montant });
                    }
                    this.dialogActe = false;
                    // Calcul reel du montant (base/coef/qte/majorations) cote backend, comme le
                    // LostFocus desktop : le prix affiche a l'ajout n'est qu'un tarif de reference.
                    await this.rafraichirMontantsActes();
                } catch (e) {
                    console.error('Erreur lors de la recherche de l\'acte :', e);
                    this.rechercheError = 'Acte introuvable ou erreur cote serveur.';
                } finally {
                    this.rechercheLoading = false;
                }
            },

            supprimerActe(ligneIndex, acteIndex) {
                this.lignesActes[ligneIndex].actes.splice(acteIndex, 1);
                // Resynchronise facfse.prest cote backend (les codes suivants se decalent).
                this.rafraichirMontantsActes();
            },

            // Recalcule les montants des actes via le backend (equivalent web du LostFocus
            // desktop). Ecrit les codes saisis dans facfse.prest, appelle FSE/saisirActe qui
            // decompose et calcule base/coefficient/quantite/montant, puis reinjecte le montant
            // calcule dans l'affichage. A appeler a chaque ajout / suppression / modif d'acte.
            async rafraichirMontantsActes() {
                try {
                    const original = this.$store.getters['FSEHandler/getFacfse'];
                    if (!original) return;
                    const facfse = JSON.parse(JSON.stringify(original));
                    const prest = this.prestArrayDepuis(facfse);
                    if (!prest) return;

                    const actes = this.lignesActes.flatMap((l) => l.actes);
                    actes.forEach((a, i) => {
                        if (prest[i + 1]) {
                            prest[i + 1].Presta_Renamed = a.code;
                            prest[i + 1].PrestaCCAM = (a.type === 'CCAM');
                        }
                    });
                    // Purge les lignes au-dela des actes saisis (codes supprimes).
                    for (let i = actes.length + 1; i < prest.length; i++) {
                        if (prest[i]) prest[i].Presta_Renamed = '';
                    }

                    const prestCalc = await Fse.saisirActe(prest);
                    if (Array.isArray(prestCalc)) {
                        actes.forEach((a, i) => {
                            const p = prestCalc[i + 1];
                            if (p) {
                                a.montant = this.parseMontant(p.Montantpaye);
                                a.backIndex = i + 1;
                            }
                        });
                    }
                } catch (e) {
                    console.error('Erreur lors du calcul des montants (saisirActe) :', e);
                }
            },

            ajouterLigne() {
                this.lignesActes.push({ date: this.dateDuJour(), actes: [] });
            },

            supprimerLigne(ligneIndex) {
                this.lignesActes.splice(ligneIndex, 1);
            },

            // === Liaison back : facfse.prest <-> lignesActes ===

            // Convertit une prestation back en acte d'affichage. Best-effort sur
            // les noms de champs (seul presta_Renamed est connu via Fse.vue) ;
            // a ajuster quand la forme reelle de facfse.prest sera confirmee
            // (voir le console.log de facfse au montage).
            acteDepuisPrest(p) {
                if (!p) return null;
                const code = p.presta_Renamed ?? p.code ?? p.Code ?? '';
                if (!code || String(code).trim() === '') return null;
                const montant = this.parseMontant(p.montant ?? p.Montant ?? p.prix ?? p.Prix ?? p.prixSs);
                const brutType = String(p.type ?? p.Type ?? '').toUpperCase();
                const type = brutType.includes('CCAM') ? 'CCAM' : 'NGAP';
                const libelle = p.libelle ?? p.Libelle ?? String(code).trim();
                return { type, code: String(code).trim(), libelle, montant };
            },

            // Liste a plat des prestations a partir d'un facfse.prest (tableau ou objet indexe).
            extrairePrestations(facfse) {
                const prest = facfse && facfse.prest;
                if (Array.isArray(prest)) return prest;
                if (prest && typeof prest === 'object') return Object.values(prest);
                return [];
            },

            // Garantit que l'acte a un sous-objet detail conforme a son type.
            assurerDetail(acte) {
                if (!acte.detail) acte.detail = detailVide(acte.type);
                return acte.detail;
            },

            estDetailOuvert(ligneIndex, acteIndex) {
                return this.detailOuvert
                    && this.detailOuvert.ligne === ligneIndex
                    && this.detailOuvert.acte === acteIndex;
            },

            toggleDetail(ligneIndex, acteIndex) {
                if (this.estDetailOuvert(ligneIndex, acteIndex)) {
                    this.detailOuvert = null;
                    return;
                }
                this.assurerDetail(this.lignesActes[ligneIndex].actes[acteIndex]);
                this.detailOuvert = { ligne: ligneIndex, acte: acteIndex };
                const acte = this.lignesActes[ligneIndex].actes[acteIndex];
                this.chargerListesChoix(acte, this.lignesActes[ligneIndex]);
            },

            async chargerListesChoix(acte, ligne) {
                this.listesChoixCCAM = null;
                if (!acte || acte.type !== 'CCAM') return;
                this.chargementChoix = true;
                try {
                    const dateActe = (ligne && ligne.date) ? ligne.date : this.dateDuJour();
                    const activite = (acte.detail && acte.detail.activite) ? acte.detail.activite : '1';
                    const r = await Acte.getListeChoixCCAM(acte.code, dateActe, activite);
                    this.listesChoixCCAM = (r && r.Item2) ? r.Item2 : {};
                } catch (e) {
                    console.error('Erreur chargement listes de choix CCAM :', e);
                    this.listesChoixCCAM = {};
                } finally {
                    this.chargementChoix = false;
                }
            },

            // Retourne la liste d'options [{valeur,libelle}] pour un champ combo.
            optionsPourChamp(champ) {
                if (!champ) return [];
                if (champ.options) return champ.options;
                if (champ.dynamique) {
                    const l = this.listesChoixCCAM && this.listesChoixCCAM[champ.dynamique];
                    return Array.isArray(l) ? l : [];
                }
                return [];
            },

            // Formate progressivement une saisie de date en jj/mm/aaaa (insertion auto des '/').
            formaterDateSaisie(valeur) {
                const chiffres = String(valeur || '').replace(/\D/g, '').slice(0, 8);
                let out = chiffres.slice(0, 2);
                if (chiffres.length >= 3) out += '/' + chiffres.slice(2, 4);
                if (chiffres.length >= 5) out += '/' + chiffres.slice(4, 8);
                return out;
            },

            // Valide une date jj/mm/aaaa (existence reelle du jour).
            dateValide(valeur) {
                const v = String(valeur || '').trim();
                if (v === '') return true; // vide = pas de date, considere non-invalide
                const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(v);
                if (!m) return false;
                const jj = +m[1], mm = +m[2], aaaa = +m[3];
                if (mm < 1 || mm > 12 || jj < 1 || jj > 31) return false;
                const d = new Date(aaaa, mm - 1, jj);
                return d.getFullYear() === aaaa && d.getMonth() === mm - 1 && d.getDate() === jj;
            },

            // Handler de saisie d'un champ date du detail : formate puis ecrit dans le detail.
            onSaisieDate(champKey, evt) {
                const formate = this.formaterDateSaisie(evt.target.value);
                const acte = this.acteDetailOuvert();
                if (acte) acte.detail[champKey] = formate;
                // garde l'affichage synchronise (au cas ou le formatage tronque)
                evt.target.value = formate;
            },

            // Le champ date EP est actif seulement si un code EP est renseigne.
            dateEpActive() {
                const acte = this.acteDetailOuvert();
                return !!(acte && acte.detail && acte.detail.ep);
            },

            // Handler de changement d'un combo du detail : recharge les listes pour
            // l'activite et vide la date EP quand le code EP devient vide.
            onChangeCombo(champ) {
                const acte = this.acteDetailOuvert();
                if (!acte) return;
                if (champ.key === 'activite') {
                    this.chargerListesChoix(acte, this.lignesActes[this.detailOuvert.ligne]);
                }
                if (champ.key === 'ep' && !acte.detail.ep) {
                    acte.detail.datep = '';
                }
            },

            acteDetailOuvert() {
                if (!this.detailOuvert) return null;
                const ligne = this.lignesActes[this.detailOuvert.ligne];
                return ligne ? ligne.actes[this.detailOuvert.acte] : null;
            },

            champsDetailOuvert() {
                const acte = this.acteDetailOuvert();
                if (!acte) return [];
                return acte.type === 'CCAM' ? this.champsCCAM : this.champsNGAP;
            },

            // Vrai si l'acte a au moins une valeur forcee (base / montant / prix).
            acteEstForce(acte) {
                const f = acte && acte.detail && acte.detail.force;
                return !!(f && (f.base || f.montant || f.prix));
            },

            // Ouvre la popup de forcage du montant remboursable AMO pour l'acte
            // dont le detail est ouvert (port de frmInfosCompPresta.vb).
            ouvrirPartAMO() {
                const acte = this.acteDetailOuvert();
                if (!acte) return;
                this.actePartAMO = acte;
                this.dialogPartAMO = true;
            },

            // Reecrit le detail dans facfse.Prest[backIndex] puis pousse au back.
            // On travaille sur une copie defensive (comme enregistrer()) pour ne pas
            // muter l'etat Vuex en place. backIndex indexe Prest (cf hydraterLignesActes).
            async appliquerDetail() {
                const acte = this.acteDetailOuvert();
                if (!acte) return;
                try {
                    const original = this.$store.getters['FSEHandler/getFacfse'];
                    if (!original) return;
                    const facfse = JSON.parse(JSON.stringify(original));
                    const prestArr = Array.isArray(facfse.Prest) ? facfse.Prest
                        : (Array.isArray(facfse.prest) ? facfse.prest : null);
                    if (prestArr && acte.backIndex != null && prestArr[acte.backIndex]) {
                        appliquerDetailDansPrest(prestArr[acte.backIndex], acte.detail);
                        await this.$store.dispatch('FSEHandler/setFacfse', facfse);
                        this.detailOuvert = null;
                    } else {
                        console.warn('Detail non rattache au back (acte saisi localement) :', acte.code);
                    }
                } catch (e) {
                    console.error('Erreur lors de l\'application du detail :', e);
                }
            },

            // Retourne le tableau facfse.prest quelle que soit la casse (le back serialise en
            // PascalCase "Prest" ; la deserialisation setFacFSE est insensible a la casse).
            prestArrayDepuis(facfse) {
                if (!facfse) return null;
                if (Array.isArray(facfse.Prest)) return facfse.Prest;
                if (Array.isArray(facfse.prest)) return facfse.prest;
                return null;
            },

            // Injecte un acte NGAP de test dans facfse.prest[1] (1-based, prest[0] = entete) si
            // aucune prestation n'y est deja saisie. Necessaire tant que la saisie d'actes
            // n'alimente pas facfse.prest : sans ca, enregreg_Click/controlechampsfse refuse la
            // FSE avec "Aucune prestation saisie dans la premiere ligne". Idempotent.
            async injecterActeTest() {
                try {
                    const original = await this.$store.dispatch('FSEHandler/fetchFacfse');
                    if (!original) return;
                    // Copie defensive (comme appliquerDetail) : on ne mute pas l'etat Vuex en place.
                    const facfse = JSON.parse(JSON.stringify(original));
                    const prest = this.prestArrayDepuis(facfse);
                    // Le back doit avoir initialise facfse.prest (tableau maxlign+2). On NE fabrique
                    // PAS de tableau tronque : enregreg_Click boucle sur maxlign slots et planterait.
                    if (!prest || prest.length < 2 || !prest[1]) {
                        console.warn('injecterActeTest : facfse.prest non initialise cote back (charger un patient d\'abord).');
                        return;
                    }
                    const codeExistant = String(prest[1].Presta_Renamed ?? prest[1].presta_Renamed ?? '').trim();
                    if (codeExistant) return; // deja une prestation en 1re ligne : rien a faire
                    prest[1].Presta_Renamed = ACTE_TEST_NGAP;
                    prest[1].PrestaCCAM = false;
                    await this.$store.dispatch('FSEHandler/setFacfse', facfse);
                } catch (e) {
                    console.error('Erreur lors de l\'injection de l\'acte de test :', e);
                }
            },

            // Charge les actes depuis le back. Ne remplace les donnees d'exemple
            // que si le back fournit reellement des prestations exploitables.
            async hydraterLignesActes() {
                try {
                    const facfse = await this.$store.dispatch('FSEHandler/fetchFacfse');
                    console.log('facfse charge :', facfse);
                    const prestArr = Array.isArray(facfse?.Prest) ? facfse.Prest
                        : (Array.isArray(facfse?.prest) ? facfse.prest : []);
                    const actes = [];
                    for (let i = 0; i < prestArr.length; i++) {
                        const p = prestArr[i];
                        if (!p) continue;
                        const type = p.PrestaCCAM ? 'CCAM' : 'NGAP';
                        const code = (type === 'CCAM' ? (p.Actedetail ?? '') : (p.Presta_Renamed ?? '')).toString().trim();
                        if (!code) continue;
                        actes.push({
                            type, code, libelle: code,
                            montant: this.parseMontant(p.Montantpaye),
                            detail: detailDepuisPrest(p),
                            backIndex: i,
                        });
                    }
                    if (actes.length) {
                        this.lignesActes = [{ date: this.dateDuJour(), actes }];
                    }
                } catch (e) {
                    console.error('Erreur lors du chargement des actes (facfse) :', e);
                }
            },

            // Reecrit les actes saisis dans le facfse puis enregistre la FSE.
            async enregistrer() {
                this.enregistrementEnCours = true;
                try {
                    const facfse = this.$store.getters['FSEHandler/getFacfse'];
                    const actes = this.lignesActes.flatMap((l) => l.actes);

                    // Copie defensive puis reecriture sur le tableau prest (PascalCase Prest ou
                    // prest). enregreg_Click lit facfse.prest[i].presta_Renamed en 1-based
                    // (prest[0] = entete), y compris pour le CCAM (SP_LostFocus decompose le code
                    // brut saisi) : on ecrit donc toujours Presta_Renamed.
                    const copie = facfse ? JSON.parse(JSON.stringify(facfse)) : null;
                    const prest = this.prestArrayDepuis(copie);
                    if (prest) {
                        actes.forEach((a, i) => {
                            if (prest[i + 1]) {
                                prest[i + 1].Presta_Renamed = a.code;
                                prest[i + 1].PrestaCCAM = (a.type === 'CCAM');
                            }
                        });
                        await this.$store.dispatch('FSEHandler/setFacfse', copie);
                    }

                    // enregistrerFse renvoie un bool (true = enregreg_Click OK cote backend).
                    const ok = await this.$store.dispatch('FSEHandler/enregistrerFse');
                    if (ok) {
                        this.afficherMessage('Feuille de soins enregistree avec succes.', 'success');
                        // Question ordonnance SCOR (port frmChoixOrdo) : specialites MK/PE/ORTHO/SF.
                        await this.proposerOrdonnanceScor();
                    } else {
                        this.afficherMessage("L'enregistrement de la feuille de soins a echoue.", 'error');
                    }
                } catch (e) {
                    console.error('Erreur lors de l\'enregistrement de la FSE :', e);
                    this.afficherMessage("Erreur lors de l'enregistrement de la feuille de soins.", 'error');
                } finally {
                    this.enregistrementEnCours = false;
                }
            },

            // Port de la "question ordonnance" desktop (ssvfse.vb, frmChoixOrdo) : apres un
            // enregistrement reussi, pour les specialites qui gerent la prescription (MK 26,
            // PE 21, ORTHO 27, SF 28/29), on propose de joindre une ordonnance a la FSE creee.
            // enregreg_Click tourne headless cote web : la decision (specialite du praticien),
            // le n° de FSE cree et l'identifiant patient sont calcules backend et renvoyes par
            // Scor/contexteEnregistrement ; on ouvre alors le dialog SCOR.
            async proposerOrdonnanceScor() {
                try {
                    const ctx = await Scor.contexteEnregistrement();
                    if (!ctx || !(ctx.success ?? ctx.Success)) return;
                    if (!(ctx.poserQuestionOrdo ?? ctx.PoserQuestionOrdo)) return;

                    const noFses = (ctx.noFses ?? ctx.NoFses) || [];
                    if (!noFses.length) return;

                    // Cas nominal mono-FSE : on propose la 1re FSE creee. L'utilisateur peut rouvrir
                    // les autres depuis la gestion des FSE (Lot.vue) si besoin.
                    this.scorNoFse = Number(noFses[0]) || 0;
                    this.scorIdent = Number(ctx.identPatient ?? ctx.IdentPatient) || 0;
                    if (this.scorNoFse > 0) this.scorOuvert = true;
                } catch (e) {
                    console.error('Erreur proposition ordonnance SCOR :', e);
                }
            },

            // Affiche un message transitoire (snackbar) en haut de l'ecran.
            afficherMessage(texte, couleur) {
                this.snackbarText = texte;
                this.snackbarColor = couleur || 'success';
                this.snackbar = true;
            },

            // === Parcours de soins (porte depuis Fse.vue) ===
            parcoursCtx() {
                return {
                    valeurActuelle: this.selectedParcours || '',
                    jeSuisMedecinTraitant: this.jeMt,
                    autreMedecinTraitant: this.autreMt,
                    aucunMedecinTraitant: this.aucunMt,
                    enAME: false,
                    regimeAccident: '',
                    dateFse: '',
                    nomOriente: this.medecinCourant().nom,
                    prenomOriente: this.medecinCourant().prenom,
                };
            },
            // Normalise selectedMedecin (objet confrere OU texte libre saisi dans le combobox)
            // en { nom, prenom, label }. Saisie libre "NOM Prenom" -> split sur le 1er espace,
            // nom en majuscules (fidele a choixnomoriente du desktop).
            medecinCourant() {
                const val = this.selectedMedecin;
                if (!val) return { nom: '', prenom: '', label: '' };
                if (typeof val === 'object') {
                    const nom = (val.nom || '').trim();
                    const prenom = (val.prenom || '').trim();
                    return { nom, prenom, label: val.label || (nom + ' ' + prenom).trim() };
                }
                const txt = String(val).trim();
                const i = txt.indexOf(' ');
                const nom = (i === -1 ? txt : txt.slice(0, i)).toUpperCase();
                const prenom = i === -1 ? '' : txt.slice(i + 1).trim();
                return { nom, prenom, label: (nom + ' ' + prenom).trim() };
            },
            onMtChange(quel) {
                if (quel === 'je') { if (this.jeMt) { this.autreMt = false; this.aucunMt = false; } }
                else if (quel === 'autre') { if (this.autreMt) { this.jeMt = false; this.aucunMt = false; } }
                else if (quel === 'aucun') { if (this.aucunMt) { this.jeMt = false; this.autreMt = false; } }
                if (this.aucunMt || this.jeMt) { this.selectedMedecin = null; this.medecins = []; this.medecinFieldKey++; }
                this.rechargerParcours();
            },
            async rechargerParcours() {
                try {
                    const res = await ParcoursDeSoin.getOptions(this.parcoursCtx());
                    const opts = (res && (res.options ?? res.Options)) || [];
                    this.parcours = opts;
                    const def = (res && (res.defaut ?? res.Defaut)) || '';
                    if (def && opts.includes(def)) this.selectedParcours = def;
                    else if (!opts.includes(this.selectedParcours)) this.selectedParcours = opts[0] || '';
                    await this.envoyerChoixParcours();
                } catch (e) {
                    console.error('Erreur chargement options parcours :', e);
                }
            },
            async envoyerChoixParcours() {
                if (!this.selectedParcours) return;
                try {
                    await ParcoursDeSoin.setChoix({ ...this.parcoursCtx(), caption: this.selectedParcours });
                } catch (e) {
                    console.error('Erreur envoi choix parcours :', e);
                }
            },

            // Bandeau reglementaire ADR : lit l'instance Formsoin serveur (UtiliseAdrText /
            // UtiliseAdrTextVisible, positionnes lors du flux VITALE/ADR) et l'affiche en haut.
            // Equivalent desktop : Formsoin.UtiliseAdrText.Visible/.Text (cf Formfsoin.vb).
            async chargerBandeauAdr() {
                try {
                    const fs = await Fse.getFormsoinInstance();
                    if (!fs) { this.utiliseAdrVisible = false; return; }
                    this.utiliseAdrVisible = !!(fs.UtiliseAdrTextVisible ?? fs.utiliseAdrTextVisible);
                    this.utiliseAdrText = (fs.UtiliseAdrText ?? fs.utiliseAdrText) || '';
                } catch (e) {
                    console.error('Erreur chargement bandeau ADR :', e);
                    this.utiliseAdrVisible = false;
                }
            },

            // Normalise la reponse getListeOriente (PascalCase possible) en {nom,prenom,label}.
            mapConfreres(rep) {
                const arr = Array.isArray(rep) ? rep : [];
                return arr.map(c => {
                    const nom = (c.nom ?? c.Nom ?? '').trim();
                    const prenom = (c.prenom ?? c.Prenom ?? '').trim();
                    return { nom, prenom, label: (nom + ' ' + prenom).trim() };
                });
            },
            // Autocomplete : recharge la liste des confreres a la frappe (>= 1 car).
            async onRechercheMedecin(entree) {
                const q = (entree || '').trim();
                if (q.length < 1) { this.medecins = []; return; }
                try {
                    const rep = await ParcoursDeSoin.getListeOriente(q);
                    this.medecins = this.mapConfreres(rep);
                } catch (e) {
                    console.error('Erreur chargement confreres :', e);
                }
            },
            // Choix d'un confrere : coche "Autre M.T" et renvoie le parcours (avec orienteur).
            onChoixMedecin() {
                const med = this.medecinCourant();
                if (med.nom || med.prenom) {
                    if (!this.autreMt) { this.autreMt = true; this.jeMt = false; this.aucunMt = false; }
                }
                this.envoyerChoixParcours();
            },
            // Suppression via la touche Suppr (fidele au desktop nomoriente_KeyDown).
            // On filtre sur la vraie touche Delete : le modificateur .delete de Vue capture
            // aussi Backspace, qui doit rester dispo pour editer le texte du combobox.
            onSupprConfrereKey(e) {
                if (e.key !== 'Delete') return;
                if (this.selectedMedecin) { e.preventDefault(); this.supprimerConfrereCourant(); }
            },
            async supprimerConfrereCourant() {
                const c = this.medecinCourant();
                if (!c.nom && !c.prenom) return;
                if (!window.confirm(`Supprimer le confrere ${c.label} ?`)) return;
                try {
                    const rep = await ParcoursDeSoin.supprimerConfrere(c.nom, c.prenom);
                    const ok = rep && (rep.success ?? rep.Success);
                    if (ok) {
                        this.selectedMedecin = null;
                        this.medecins = [];
                        this.medecinFieldKey++;
                        await this.envoyerChoixParcours();
                    }
                } catch (e) {
                    console.error('Erreur suppression confrere :', e);
                }
            },

            // === IMT (Interrogation Medecin Traitant / IMTi) ===
            // Applique le resultat IMT du store dans la zone parcours : si un MT est trouve, pre-remplit
            // le medecin oriente (coche "Autre M.T"). Fidele au desktop retourIMT : le remplissage
            // n'ecrase le champ que s'il est vide (nomoriente == ""), sauf force=true (bouton manuel).
            appliquerResultatIMT(force) {
                const med = this.medecinIMT;
                if (!med) return;
                if ((med.statutExec || '') === 'EnCoursDExecution') return; // pas encore termine
                const trouve = med.code === '00' || (med.statutResult || '') === 'MédecinTrouvé';
                if (!trouve) return;
                if (!med.nom && !med.prenom) return;
                const courant = this.medecinCourant();
                if (!force && (courant.nom || courant.prenom)) return; // ne pas ecraser une saisie
                const nom = (med.nom || '').trim();
                const prenom = (med.prenom || '').trim();
                this.selectedMedecin = { nom, prenom, label: (nom + ' ' + prenom).trim() };
                if (!this.autreMt) { this.autreMt = true; this.jeMt = false; this.aucunMt = false; }
                this.medecinFieldKey++;
                this.envoyerChoixParcours();
            },
            // Re-interrogation manuelle du medecin traitant (equivalent clic btnIMT desktop).
            // Relance l'appel IMT pour le beneficiaire actif puis poll le resultat.
            async interrogerImt() {
                if (this.imtLoading) return;
                this.imtLoading = true;
                try {
                    const idx = this.indexBenefActif ?? 0;
                    await Assertion.generateImtResponse(idx);
                    await this.$store.dispatch('ChoixBenecStore/pollReponseIMT');
                    this.appliquerResultatIMT(true);
                } catch (e) {
                    console.error('Interrogation IMT :', e);
                } finally {
                    this.imtLoading = false;
                }
            },
        },

        async mounted() {
            // Liste des codes CCAM pour l'autocomplete de la popup
            try {
                this.codesCCAM = await Acte.getCodeActe();
            } catch (e) {
                console.error('Erreur lors du chargement des codes CCAM :', e);
            }

            // Charge les parametres FSE puis les actes existants depuis le back
            this.$store.dispatch('FSEHandler/initParams');
            // Injecte un acte NGAP de test dans facfse.prest[1] si vide (workaround tant que la
            // saisie d'actes n'ecrit pas dans facfse.prest), puis affiche les actes du back.
            await this.injecterActeTest();
            await this.hydraterLignesActes();

            // Charge les options du parcours de soins au demarrage (try/catch interne)
            this.rechargerParcours();

            // Bandeau reglementaire ADR (visible si la facture est realisee a l'aide du service ADR)
            await this.chargerBandeauAdr();

            // IMT : le poll a pu se terminer avant le montage de NewFSE (flux Vitale/degrade lance
            // en amont) -> applique le resultat deja present dans le store.
            this.appliquerResultatIMT(false);
        },
    });
</script>
